/**
 * @internal
 */
import React, { memo, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, m as motion } from 'framer-motion';
import { createTransition, MotionDurationsSeconds, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { css } from '@emotion/react';
import { PointerContentTypes, } from '@polestar/component-warehouse-react/components/pointer-replace/PointerReplaceContext';
import generateId from '@polestar/component-warehouse-react/helpers/generateId';
import { ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import { DarkThemeProvider, LightThemeProvider, } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
const transition = createTransition({
    ease: MotionEasings.easeOut,
    duration: MotionDurationsSeconds.xSlow,
});
const PointerReplace = memo(({ mouseDown, content, positionX, positionY, opacity, iconOffsetY, iconOffsetX }) => {
    const [internalContent, setInternalContent] = useState(null);
    const reference = useRef('');
    useEffect(() => content === null || content === void 0 ? void 0 : content.on('change', (latestContent) => {
        // make an unique id to ensure that elements that are set shortly after each other will be removed and not share the same React.key which AnimatePresence uses to determine what to remove or keep.
        reference.current = Math.floor(Math.random() * 1000000000).toString();
        setInternalContent(latestContent
            ? {
                key: generateId(),
                ...latestContent,
            }
            : null);
    }), [content]);
    const [mouseDownScale, setMouseDownScale] = useState(1);
    const scale = ((internalContent === null || internalContent === void 0 ? void 0 : internalContent.type) === PointerContentTypes.empty
        ? 0.25
        : (internalContent === null || internalContent === void 0 ? void 0 : internalContent.type) === PointerContentTypes.text
            ? 1
            : 0.5) * mouseDownScale;
    useEffect(() => mouseDown === null || mouseDown === void 0 ? void 0 : mouseDown.on('change', (latestMouseDown) => {
        setMouseDownScale(latestMouseDown ? 0.8 : 1);
    }), [mouseDown]);
    const ThemeWrapper = (internalContent === null || internalContent === void 0 ? void 0 : internalContent.theme) === ThemeTypes.dark ? DarkThemeProvider : LightThemeProvider;
    return (React.createElement(Portal, { zIndex: LayoutIndexes.popover + 1 },
        React.createElement(AnimatePresence, null, !!internalContent && (React.createElement(ThemeWrapper, { key: `pointer-replace-${reference.current}` },
            React.createElement(Pointer, { key: "pointer", style: {
                    x: positionX,
                    y: positionY,
                }, initial: { opacity: 0, scale: scale / 2 }, animate: { opacity: opacity.get(), scale }, exit: { opacity: 0, scale: scale / 2 }, transition: transition },
                React.createElement(PointerInner, null,
                    React.createElement(AnimatePresence, null, internalContent.type === PointerContentTypes.icon ? (React.createElement(IconWrapper, { key: internalContent.key, initial: { opacity: 0, scale: 0.5 }, animate: { opacity: 1, scale: 2 / mouseDownScale }, exit: { opacity: 0, scale: 0.5 }, transition: transition, offset: { x: iconOffsetX, y: iconOffsetY } },
                        React.createElement(PointerIcon, { icon: internalContent.content, pairedWithText: false, size: IconSizes.small }))) : internalContent.type === PointerContentTypes.text ? (React.createElement(IconWrapper, { key: internalContent.key, initial: { opacity: 0, scale: 0.5 }, animate: { opacity: 1, scale: 1 / mouseDownScale }, exit: { opacity: 0, scale: 0.5 }, transition: transition, offset: { x: iconOffsetX, y: iconOffsetY } },
                        React.createElement(StyledParagraph, null, internalContent.content))) : null))))))));
});
PointerReplace.displayName = 'PointerReplace';
export default PointerReplace;
const dimension = createCustomSpacing(5);
const dimensionHalf = createCustomSpacing(2.5);
const Pointer = styled(motion.div)(({ theme }) => css `
    position: fixed;
    z-index: 2;
    width: ${dimension};
    height: ${dimension};
    margin: -${dimensionHalf};
    border-radius: 50%;
    background: ${theme.pointerReplace.backgroundColor};
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transform-origin: 50% 50%;
    display: none;
    color: ${theme.pointerReplace.color};
    @media (hover: hover) {
      display: flex;
    }
    /* Fixes pointer replace disappearing in RTL */
    [dir='rtl'] & {
      left: 0;
    }
  `);
const PointerInner = styled.div `
  width: ${dimensionHalf};
  height: ${dimensionHalf};
  transform: translateZ(0);
  position: relative;
`;
const IconWrapper = styled(motion.div, shouldNotForwardProps(false, 'offset'))(({ offset }) => css `
    position: absolute;
    inset-block-start: ${offset.y.get()}px;
    inset-inline-start: ${offset.x.get()}px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `);
const PointerIcon = styled(Icon) `
  [dir='rtl'] & {
    transform: scaleX(-1);
  }
`;
const StyledParagraph = styled.span `
  white-space: nowrap;
`;
